import { HttpResourceRef } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UserCommunicationPreferencesResponse, UsersProfileService } from '@customer-portal/api';
import { AccountState } from '@customer-portal/shared/account/data-access';

import { CommunicationPreference, CommunicationPreferenceId } from './communication-preferences.models';

@Injectable()
export class CommunicationPreferencesStore {
  private readonly usersProfileService = inject(UsersProfileService);
  private readonly accountState = inject(AccountState);

  private readonly updateResource = this.usersProfileService.usersProfileUpdateCommunicationPreferences();

  private readonly userId = computed(() => this.accountState.$account()?.sub ?? '');

  private readonly onUserIdChange = effect(() => {
    this.usersProfileService.usersProfileGetCommunicationPreferencesParams.userId.set(this.userId());
  });

  // The generated GET client assumes HAL, but this endpoint returns plain JSON.
  public readonly resource = this.usersProfileService.usersProfileGetCommunicationPreferencesResource as HttpResourceRef<
    UserCommunicationPreferencesResponse | { content: UserCommunicationPreferencesResponse } | undefined
  >;

  public readonly preferences = computed((): readonly CommunicationPreference[] => {
    if (!this.resource.hasValue()) {
      return [];
    }

    // Support both plain JSON and responses wrapped by `apiHalTransform`.
    const value = this.resource.value();
    const response = value && 'content' in value ? value.content : value;

    return (response?.preferences ?? []).map((preference) => ({
      id: preference.name as CommunicationPreferenceId,
      enabled: preference.enabled ?? false,
    }));
  });

  public readonly isLoading = this.resource.isLoading;
  public readonly loadError = this.resource.error;
  public readonly isSaving = this.updateResource.isLoading;
  public readonly saveError = this.updateResource.error;
  public readonly allEnabled = computed(() => this.preferences().length > 0 && this.preferences().every((preference) => preference.enabled));

  public async save(preferences: readonly CommunicationPreference[]): Promise<boolean> {
    if (preferences.length === 0) {
      return true;
    }

    const response = await this.updateResource.invoke({
      userId: signal(this.userId()),
      body: signal({ preferences: preferences.map((preference) => ({ name: preference.id, enabled: preference.enabled })) }),
    });

    if (this.updateResource.status() !== 'resolved' || !response) {
      return false;
    }

    this.resource.set(response);
    return true;
  }
}
